import { useMemo } from 'react';
import { useSelector } from "react-redux";
import { selectAuthentication } from "../../services/authorization/slices";
import { compareAuthentication } from "../../services/util/selectorEquality";
import { useNavigate, useLocation } from 'react-router-dom';
import { AUTH_ROLES } from "../../util/constants";

/**
 * Centralized Authorization Hook
 * Single source of truth for all permissions and role-based access control
 * 
 * User Types:
 * 1. ADMIN - Honors & Scholars admins with ADMIN-ACCESS role
 * 2. STUDENT_EMPLOYEE - Support staff with STUDENT-EMPLOYEE role  
 * 3. REGULAR_USER - Everyone else (reviewers, students, etc.)
 */
function useRoleAuthentication({ redirectOnUnauthorized = true } = {}) {
    const nav = useNavigate();
    const location = useLocation();
    const authenticationState = useSelector(selectAuthentication, compareAuthentication);

    const isLoggedIn = authenticationState.isLoggedIn;
    const user = authenticationState.user ?? {};
    const roles = user.applicationRoles ?? [];

    // User type identification - simplified to 3 types
    const userType = useMemo(() => {
        if (roles.includes(AUTH_ROLES.ADMIN_ACCESS)) return 'ADMIN';
        if (roles.includes(AUTH_ROLES.STUDENT_EMPLOYEE)) return 'STUDENT_EMPLOYEE';
        return 'REGULAR_USER';
    }, [roles]);

    // Centralized permissions - single source of truth
    const permissions = useMemo(() => {
        const isAdmin = userType === 'ADMIN';
        const isStudentEmployee = userType === 'STUDENT_EMPLOYEE';
        const isRegularUser = userType === 'REGULAR_USER';

        return {
            // Navigation permissions
            canSeeAdminDashboardLink: isAdmin || isStudentEmployee,
            canSeeDashboardLink: true, // Everyone can see their own dashboard
            
            // Route access permissions
            canAccessAdminDashboard: isAdmin || isStudentEmployee,
            canAccessStudentDashboard: true, // Everyone can access their own dashboard
            
            // Admin functionality permissions
            canSearchStudents: isAdmin || isStudentEmployee,
            canViewAnyStudentDashboard: isAdmin || isStudentEmployee,
            
            // Future extensibility - student employee restrictions
            canViewStudentPersonalInfo: isAdmin, // Student employees might be restricted later
            canPerformAdminActions: isAdmin, // Only full admins can perform actions
            
            // User type flags
            isAdmin,
            isStudentEmployee,
            isRegularUser,
            
            // Legacy compatibility for existing code
            isAnyAdmin: isAdmin || isStudentEmployee,
        };
    }, [userType]);

    // Route protection - redirect unauthorized users trying to access admin routes
    const currentPath = location.pathname;
    const isAdminRoute = currentPath.startsWith('/admin/');
    
    if (isLoggedIn && isAdminRoute && !permissions.canAccessAdminDashboard) {
        if (redirectOnUnauthorized) {
            // Redirect to main dashboard instead of unauthorized page
            nav("/");
        }
    }

    return {
        // Authentication state
        ...authenticationState,
        isLoggedIn,
        user,
        roles,
        userType,
        
        // All permissions - semantic and clear
        ...permissions,
    };
}

export default useRoleAuthentication;